import {
  Alert,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import EurCurrencyField from '@/components/form/EurCurrencyField.tsx';
import AdvancedSection from '@/features/investments/form/components/AdvancedSection.tsx';
import FormSection from '@/features/investments/form/components/FormSection.tsx';
import {
  FinancingDecision,
  FinancingInputMode,
  InvestmentFormData,
} from '@/features/investments/form/investment-form.schema.ts';
import { deriveFinancedPercentage } from '@/features/investments/form/utils.ts';
import { formatCurrency, formatPercentage } from '@/utils/misc.ts';
import PercentageField from '@/components/form/PercentageField.tsx';

const calculateMonthlyPayment = (
  principal: number,
  annualRate: number,
  termYears: number
): number => {
  if (principal <= 0 || annualRate < 0 || termYears <= 0) {
    return 0;
  }

  const monthlyRate = annualRate / 12;
  const numberOfPayments = termYears * 12;

  if (monthlyRate === 0) {
    return principal / numberOfPayments;
  }

  return (
    (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
    (Math.pow(1 + monthlyRate, numberOfPayments) - 1)
  );
};

const FinancingSection = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<InvestmentFormData>();

  const [
    financingDecision,
    financingInputMode,
    acquisitionPrice,
    loanAmount,
    termYears,
    rateType,
    fixedRate,
    variableSpread,
    variableEuribor,
  ] = useWatch({
    control,
    name: [
      'financingDecision',
      'financingInputMode',
      'acquisitionPrice',
      'loanAmount',
      'termYears',
      'rateType',
      'fixedRate',
      'variableSpread',
      'variableEuribor',
    ],
  });

  const hasFinancingEnabled = financingDecision === 'YES';
  const isEstimateMode = financingInputMode === 'ESTIMATE_PAYMENT';
  const financedPercentage = deriveFinancedPercentage(loanAmount, acquisitionPrice);
  const annualRate =
    rateType === 'FIXED'
      ? (fixedRate ?? null)
      : rateType === 'VARIABLE' && variableSpread !== null && variableEuribor !== null
        ? variableSpread + variableEuribor
        : null;
  const estimatedMonthlyPayment =
    isEstimateMode &&
    loanAmount !== null &&
    termYears !== null &&
    annualRate !== null &&
    loanAmount > 0 &&
    termYears > 0
      ? Math.round(calculateMonthlyPayment(loanAmount, annualRate, termYears))
      : null;

  return (
    <FormSection
      title="6. Financiación"
      description="Indica si habrá hipoteca o déjalo pendiente para completarlo más adelante."
    >
      <Controller
        name="financingDecision"
        control={control}
        render={({ field }) => (
          <FormControl error={!!errors.financingDecision}>
            <FormLabel>¿Tendrá financiación?</FormLabel>
            <RadioGroup
              row
              value={field.value}
              onChange={(_, value) => field.onChange(value as FinancingDecision)}
            >
              <FormControlLabel value="PENDING" control={<Radio />} label="Pendiente" />
              <FormControlLabel value="YES" control={<Radio />} label="Sí" />
              <FormControlLabel value="NO" control={<Radio />} label="No" />
            </RadioGroup>
            {errors.financingDecision ? (
              <Typography variant="caption" color="error">
                {errors.financingDecision.message}
              </Typography>
            ) : null}
          </FormControl>
        )}
      />

      {hasFinancingEnabled ? (
        <Stack spacing={2}>
          <Controller
            name="financingInputMode"
            control={control}
            render={({ field }) => (
              <FormControl error={!!errors.financingInputMode}>
                <FormLabel>Cómo quieres indicar la financiación</FormLabel>
                <RadioGroup
                  value={field.value}
                  onChange={(_, value) => field.onChange(value as FinancingInputMode)}
                >
                  <FormControlLabel
                    value="MANUAL_PAYMENT"
                    control={<Radio />}
                    label="Ya conozco la cuota mensual"
                  />
                  <FormControlLabel
                    value="ESTIMATE_PAYMENT"
                    control={<Radio />}
                    label="Quiero estimarla desde la hipoteca"
                  />
                </RadioGroup>
                {errors.financingInputMode ? (
                  <Typography variant="caption" color="error">
                    {errors.financingInputMode.message}
                  </Typography>
                ) : null}
              </FormControl>
            )}
          />

          {financingInputMode === 'MANUAL_PAYMENT' ? (
            <Controller
              name="monthlyDebtService"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField
                  label="Cuota mensual"
                  value={value}
                  onChange={onChange}
                  error={!!errors.monthlyDebtService}
                  helperText={
                    errors.monthlyDebtService?.message ??
                    'Opcionalmente puedes completar más detalles después'
                  }
                />
              )}
            />
          ) : null}

          {isEstimateMode ? (
            <>
              <Controller
                name="loanAmount"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <EurCurrencyField
                    label="Importe financiado"
                    value={value}
                    onChange={onChange}
                    error={!!errors.loanAmount}
                    helperText={errors.loanAmount?.message}
                  />
                )}
              />

              <Controller
                name="termYears"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Plazo (años)"
                    fullWidth
                    error={!!errors.termYears}
                    helperText={errors.termYears?.message}
                    inputProps={{ min: 1, step: 1 }}
                    value={field.value ?? ''}
                    onChange={(event) =>
                      field.onChange(
                        event.target.value === '' ? null : Number.parseInt(event.target.value, 10)
                      )
                    }
                  />
                )}
              />

              <Controller
                name="rateType"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Tipo fijo / variable"
                    fullWidth
                    error={!!errors.rateType}
                    helperText={errors.rateType?.message}
                  >
                    <MenuItem value="">Selecciona una opción</MenuItem>
                    <MenuItem value="FIXED">Fijo</MenuItem>
                    <MenuItem value="VARIABLE">Variable</MenuItem>
                  </TextField>
                )}
              />

              <AdvancedSection description="TIN, diferencial y detalle fino de la hipoteca">
                {rateType === 'FIXED' ? (
                  <Controller
                    name="fixedRate"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <PercentageField
                        label="TIN"
                        value={value}
                        onChange={onChange}
                        error={!!errors.fixedRate}
                        helperText={errors.fixedRate?.message}
                      />
                    )}
                  />
                ) : null}

                {rateType === 'VARIABLE' ? (
                  <>
                    <Controller
                      name="variableEuribor"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <PercentageField
                          label="Euríbor"
                          value={value}
                          onChange={onChange}
                          error={!!errors.variableEuribor}
                          helperText={errors.variableEuribor?.message}
                        />
                      )}
                    />
                    <Controller
                      name="variableSpread"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <PercentageField
                          label="Diferencial"
                          value={value}
                          onChange={onChange}
                          error={!!errors.variableSpread}
                          helperText={errors.variableSpread?.message}
                        />
                      )}
                    />
                  </>
                ) : null}

                {financedPercentage !== undefined ? (
                  <Alert severity="info">
                    Porcentaje financiado derivado: {formatPercentage(financedPercentage)}
                  </Alert>
                ) : null}
              </AdvancedSection>
            </>
          ) : null}

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {loanAmount !== null ? (
              <Chip label={`Capital: ${formatCurrency(loanAmount)}`} variant="outlined" />
            ) : null}
            {financedPercentage !== undefined ? (
              <Chip
                label={`% financiado: ${formatPercentage(financedPercentage)}`}
                variant="outlined"
              />
            ) : null}
            {estimatedMonthlyPayment !== null ? (
              <Chip
                color="success"
                label={`Cuota estimada: ${formatCurrency(estimatedMonthlyPayment)}`}
              />
            ) : null}
          </Stack>
        </Stack>
      ) : null}
    </FormSection>
  );
};

export default FinancingSection;
